import { LayoutProvidedReportDecoratorProps, LookupModelSearchEvent, Layout, ReportDecorator, SlideoutDecorator } from "@mcleod/components";
import { StringUtil, Alignment } from "@mcleod/core";
import { AutogenLayoutCollectionsReport } from "./autogen/AutogenLayoutCollectionsReport";
import { ModelCustomer } from "../models/ModelCustomer";

export class CollectionsReport extends AutogenLayoutCollectionsReport {
    customerIds: string[] = [];

    public override get reportDecoratorProps(): Partial<LayoutProvidedReportDecoratorProps> {
        return {
            reportPreviewTitle: "Collections Report"
        };
    }

    onLoad() {
        this.textboxSortBy.selectedItem = this.textboxSortBy.items[0];
        this.textboxIncludeCustomers.selectedItem = this.textboxIncludeCustomers.items[0];
        this.textboxShortPaidInvoice.selectedItem = this.textboxShortPaidInvoice.items[0];
        this.textboxReportBy.selectedItem = this.textboxReportBy.items[0];
        this.textboxPastDue.text = "0";
    }

    public setupMultipleCustomerData(customerIds: string[]) {
        if (!Array.isArray(customerIds) || customerIds.length === 0) {
            return;
        }

        this.customerIds = customerIds.map(id => id.trim()).filter(id => !StringUtil.isEmptyString(id));
        this.setupMultipleCustomersDisplayWithModelData();
    }

    private async setupMultipleCustomersDisplayWithModelData() {
        try {
            // Fetch all customer data
            const customerPromises = this.customerIds.map(customerId =>
                new ModelCustomer().searchSingle({ id: customerId }, "lme/ar/Customer")
            );

            const customerRows = await Promise.all(customerPromises);
            const validCustomers = customerRows.filter(row => row != null);

            if (validCustomers.length > 0) {
                // Create lookup model data for multiple customers
                const lookupModelData = validCustomers.map(customerRow => ({
                    "id": customerRow.get("id"),
                    "name": customerRow.get("name")
                }));

                // Set the comma-separated value and the lookup model data
                const customerIdValue = this.customerIds.join(",");
                this.sourceCollectionReport.searchRow.setValueAndLookupModelData("customer_id", customerIdValue, lookupModelData);
                this.excludabletextboxCustomer.displayData(this.sourceCollectionReport.searchRow, null, 0);
            }
        } catch (error) {
            console.error("Error setting up multiple customer data:", error);
            // Fallback to just setting the raw value
            const customerIdValue = this.customerIds.join(",");
            this.sourceCollectionReport.searchRow.set("customer_id", customerIdValue);
            this.excludabletextboxCustomer.displayData(this.sourceCollectionReport.searchRow, null, 0);
        }
    }

    textboxCategoryTextboxBeforeLookupMode(event: LookupModelSearchEvent) {
        event.filter.customer = "Y"
    }

    textboxCustomersBeforeLookupModelSearch(event: LookupModelSearchEvent) {
        event.filter.type_of = "C"
        event.filter.is_active = "Y"
    }

    switchSwitchReportTypeOnChange(){
        if(!this.switchSwitchReportType.checked){
            this.checkboxPageBreak.enabled = false;
            this.checkboxPageBreak.checked = false;
            this.switchIncCallHist.enabled = false;
            this.switchIncCallHist.checked = false;
            this.textboxCallHistStatrtDate.enabled = false;
            this.checkboxPrintExtend.enabled = false;
            this.checkboxPrintExtend.checked = false;
            this.checkboxPrintBol.enabled = false;
        }
        else {
            this.checkboxPageBreak.enabled = true;
            this.switchIncCallHist.enabled = true;
            this.textboxCallHistStatrtDate.enabled = true;
            this.checkboxPrintExtend.enabled = true;
            this.checkboxPrintBol.enabled = true;
        }
    }

    checkboxInclCallHistOnChange(){
        if(!this.switchIncCallHist.checked){
            this.textboxCallHistStatrtDate.enabled = false;
            this.checkboxPrintExtend.enabled = false;
        }
        else{
            this.textboxCallHistStatrtDate.enabled = true;
            this.checkboxPrintExtend.enabled = true;
        }
    }

    textboxIncludeCustomersOnChange(){
        switch(this.textboxIncludeCustomers.selectedItem?.displayValue) {
            case("With Outstanding Balances"):
                this.textboxPastDue.enabled = false;
                this.checkboxIncludeOpenCreadit.enabled = true;
                this.textboxShortPaidInvoice.enabled = true;
                break;
            case("With Past Due Balances"):
                this.textboxPastDue.enabled = true;
                this.checkboxIncludeOpenCreadit.enabled = false;
                this.checkboxIncludeOpenCreadit.checked = false;
                this.textboxShortPaidInvoice.enabled = true;
                break;
            case("Over Collection Threshold"):
                this.textboxPastDue.enabled = false;
                this.checkboxIncludeOpenCreadit.enabled = false;
                this.textboxShortPaidInvoice.selectedItem = this.textboxShortPaidInvoice.items[0]
                this.textboxShortPaidInvoice.enabled = false;
                break;
        }
    }

    public static showCollectionsReportWithDecorator(customerIds: string[]): void {
        const collectionsReport = Layout.getLayout("lme/ar/reports/CollectionsReport") as CollectionsReport;
        collectionsReport.addLayoutLoadListener(() => {
            if (customerIds && customerIds.length > 0) {
                collectionsReport.setupMultipleCustomerData(customerIds);
            }
            collectionsReport.mainDataSource?.resetDefaultSearchRowToCurrent();
        });

        this.buildCollectionsSlideoutTitle(customerIds).then(title => {
            this.showReportInSlideout(collectionsReport, title);
        });
    }

    private static showReportInSlideout(reportLayout: Layout, title: string) {
        const reportDecorator = new ReportDecorator({ layout: reportLayout });
        const layoutWrapper = new Layout({
            needsServerLayout: false,
            components: [ reportDecorator ],
            paddingTop: 12,
            paddingRight: 12,
            paddingBottom: 12
        });

        new SlideoutDecorator({
            layout: layoutWrapper,
            width: "95%",
            fillVerticalSpace: true,
            fillHorizontalSpace: true,
            title,
            transitionOptions: {
                direction: Alignment.BOTTOM
            }
        });
    }

    private static async buildCollectionsSlideoutTitle(customerIds: string[]): Promise<string> {
        if (!customerIds || customerIds.length === 0) {
            return "Collections Report";
        }

        if (customerIds.length === 1) {
            try {
                const customerRow = await new ModelCustomer().searchSingle({ id: customerIds[0] }, "lme/ar/Customer");
                if (customerRow) {
                    const customerName = customerRow.get("name");
                    return `Collections Report - ${customerName}`;
                }
            } catch (error) {
                console.error("Error fetching customer name for title:", error);
            }
            return `Collections Report - Customer ${customerIds[0]}`;
        } else {
            return `Collections Report - ${customerIds.length} Customers`;
        }
    }
}
